import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { apiClient } from '@/shared/lib/api-client'
import { DURABLE_QUERY, OFFLINE_MUTATION } from '@/shared/lib/query-profiles'
import type { Score, ScoreWithJudge, JudgeState } from '@app/shared/types/entities'

// --- Dirty flag helpers ---

export type ScoreWithDirty = Score & { _dirty?: boolean; _pendingSubmit?: boolean }
export type JudgeStateWithDirty = JudgeState & { _dirtyActivations?: string[] }

/** Check if a cached score has unsent local changes. */
export function isScoreDirty(
  queryClient: ReturnType<typeof useQueryClient>,
  participationId: string,
): boolean {
  const cached = queryClient.getQueryData<ScoreWithDirty>(['my-score', participationId])
  return cached?._dirty === true
}

/** Check if ANY cached my-score query is dirty. */
export function getDirtyScores(
  queryClient: ReturnType<typeof useQueryClient>,
): Array<{ participationId: string; score: ScoreWithDirty }> {
  const dirty: Array<{ participationId: string; score: ScoreWithDirty }> = []
  const cache = queryClient.getQueryCache()
  for (const query of cache.getAll()) {
    if (query.queryKey[0] === 'my-score' && query.state.data) {
      const score = query.state.data as ScoreWithDirty
      if (score._dirty) {
        dirty.push({ participationId: query.queryKey[1] as string, score })
      }
    }
  }
  return dirty
}

/** Get participationIds activated offline by the super judge. */
export function getDirtyActivations(
  queryClient: ReturnType<typeof useQueryClient>,
  competitionId: string,
): string[] {
  const cached = queryClient.getQueryData<JudgeStateWithDirty>([
    'competitions',
    competitionId,
    'judge-state',
  ])
  return cached?._dirtyActivations ?? []
}

// --- Judge-facing hooks ---

export function useMyScore(participationId: string) {
  return useQuery({
    queryKey: ['my-score', participationId],
    queryFn: async () => {
      const res = await apiClient.get<{ score: Score }>(
        `/api/participations/${participationId}/my-score`,
      )
      return res.score
    },
    ...DURABLE_QUERY,
    enabled: Boolean(participationId),
  })
}

export function useUpdateMyScoreQuestion(participationId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['my-score', participationId, 'question'],
    ...OFFLINE_MUTATION,

    mutationFn: async ({
      questionNumber,
      mistakes,
    }: {
      questionNumber: number
      mistakes: string[]
    }) => {
      const res = await apiClient.put<{ score: Score }>(
        `/api/participations/${participationId}/my-score/question/${questionNumber}`,
        { mistakes },
      )
      return res.score
    },

    onMutate: async ({ questionNumber, mistakes }) => {
      await queryClient.cancelQueries({ queryKey: ['my-score', participationId] })
      const previous = queryClient.getQueryData<Score>(['my-score', participationId])

      queryClient.setQueryData<Score>(['my-score', participationId], (old) => {
        if (!old) return old
        const questionsData = [...old.questionsData]
        const idx = questionsData.findIndex((q) => q.question === questionNumber)
        if (idx >= 0) {
          questionsData[idx] = { question: questionNumber, mistakes }
        } else {
          questionsData.push({ question: questionNumber, mistakes })
        }
        return { ...old, questionsData }
      })

      return { previous }
    },

    onSuccess: (serverScore) => {
      queryClient.setQueryData<Score>(['my-score', participationId], serverScore)
    },

    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(['my-score', participationId], ctx.previous)
      }
    },
  })
}

export function useUpdateMyScoreStage2(participationId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['my-score', participationId, 'stage2'],
    ...OFFLINE_MUTATION,

    mutationFn: async (values: Record<string, number>) => {
      const res = await apiClient.put<{ score: Score }>(
        `/api/participations/${participationId}/my-score/stage2`,
        { values },
      )
      return res.score
    },

    onMutate: async (values) => {
      await queryClient.cancelQueries({ queryKey: ['my-score', participationId] })
      const previous = queryClient.getQueryData<Score>(['my-score', participationId])

      queryClient.setQueryData<Score>(['my-score', participationId], (old) => {
        if (!old) return old
        return { ...old, stage2Data: { ...old.stage2Data, ...values } }
      })

      return { previous }
    },

    onSuccess: (serverScore) => {
      queryClient.setQueryData<Score>(['my-score', participationId], serverScore)
    },

    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(['my-score', participationId], ctx.previous)
      }
    },
  })
}

export function useSubmitMyScore(participationId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const res = await apiClient.post<{ score: Score }>(
        `/api/participations/${participationId}/my-score/submit`,
      )
      return res.score
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-score', participationId] })
    },
  })
}

// --- Admin-facing hooks ---

export function useScore(scoreId: string) {
  return useQuery({
    queryKey: ['scores', scoreId],
    queryFn: async () => {
      const res = await apiClient.get<{ score: ScoreWithJudge }>(`/api/scores/${scoreId}`)
      return res.score
    },
    enabled: Boolean(scoreId),
  })
}

export function useParticipationScores(participationId: string) {
  return useQuery({
    queryKey: ['participations', participationId, 'scores'],
    queryFn: async (): Promise<ScoreWithJudge[]> => {
      const res = await apiClient.get<{ scores: ScoreWithJudge[] }>(
        `/api/participations/${participationId}/scores`,
      )
      return res.scores
    },
    enabled: Boolean(participationId),
  })
}

export function useOverrideScoreQuestion(scoreId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      questionNumber,
      mistakes,
    }: {
      questionNumber: number
      mistakes: string[]
    }) => {
      const res = await apiClient.put<{ score: ScoreWithJudge }>(
        `/api/scores/${scoreId}/question/${questionNumber}`,
        { mistakes },
      )
      return res.score
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scores', scoreId] })
    },
  })
}

export function useOverrideScoreStage2(scoreId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (values: Record<string, number>) => {
      const res = await apiClient.put<{ score: ScoreWithJudge }>(`/api/scores/${scoreId}/stage2`, {
        values,
      })
      return res.score
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scores', scoreId] })
    },
  })
}

export function useAdminCreateScore(participationId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (judgeParticipationId: string) => {
      const res = await apiClient.post<{ score: ScoreWithJudge }>(
        `/api/participations/${participationId}/scores`,
        { judgeParticipationId },
      )
      return res.score
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['participations', participationId, 'scores'],
      })
    },
  })
}

export function useAdminSubmitScore(participationId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (scoreId: string) => {
      const res = await apiClient.post<{ score: ScoreWithJudge }>(`/api/scores/${scoreId}/submit`)
      return res.score
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['participations', participationId, 'scores'],
      })
      queryClient.invalidateQueries({
        queryKey: ['competitions'],
      })
    },
  })
}
